import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Icon,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { FundingRequestWithBaseDetailsFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { useApproveFundingRequestMutation } from 'capitalProvider/domains/fundingRequests/requests/fundingRequests.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import {
  errorToastConfig,
  errorToastId,
  fundingRequestKeyToLabel,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { dollarNumberWithCommas } from 'packages/common-frontend/utils';
import { GetFundingRequestWithBaseDetailsDocumentGQLType } from 'packages/funding-requests/requests/requests.gql.generated';
import { FiCheckCircle } from 'react-icons/fi';

type Props = {
  fundingRequest: FundingRequestWithBaseDetailsFragmentGQLType;
  disabled?: boolean;
};

export const ApproveFundingRequestButton: React.FC<Props> = ({
  fundingRequest,
  disabled,
}) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [approveFundingRequest, { loading }] = useApproveFundingRequestMutation(
    {
      variables: { fundingRequestId: fundingRequest.id },
      refetchQueries: [
        {
          query: GetFundingRequestWithBaseDetailsDocumentGQLType,
          variables: { fundingRequestId: fundingRequest.id },
        },
      ],
    },
  );

  const cancelRef = React.useRef();

  return (
    <>
      <Button
        colorScheme="green"
        aria-label="approve funding request"
        isLoading={loading}
        onClick={onOpen}
        disabled={!!disabled}
      >
        <Icon as={FiCheckCircle} mr="5px" />
        Approve Funding Request
      </Button>
      <AlertDialog
        leastDestructiveRef={cancelRef.current}
        isOpen={isOpen}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Approve Funding Request
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you would like to approve the funding request for{' '}
              {fundingRequest.creditFacilityName}?
              <Table variant="simple" mt="10px">
                <Thead
                  background={colors.tableHeader}
                  borderBottom="1px solid lightgrey"
                  padding="8px 0"
                >
                  <Tr>
                    <Th textAlign="left">Transaction</Th>
                    <Th textAlign="center">Amount</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {_.map(
                    fundingRequest.fundingRequestComponents,
                    ({ id, type, amount }) => {
                      return (
                        !!amount && (
                          <Tr key={`row-${id}`}>
                            <Td textAlign="left">
                              {fundingRequestKeyToLabel[type]}
                            </Td>
                            <Td textAlign="center">
                              {dollarNumberWithCommas(amount)}
                            </Td>
                          </Tr>
                        )
                      );
                    },
                  )}
                </Tbody>
              </Table>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef.current} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={async () => {
                  try {
                    const { data } = await approveFundingRequest();

                    if (data) {
                      successToastConfig.title = 'Funding Request Approved';
                      toast(successToastConfig);
                    }
                  } catch (err: any) {
                    if (!toast.isActive(errorToastId)) {
                      errorToastConfig.title = `Error approving funding request`;
                      toast(errorToastConfig);
                    }
                    onClose();
                    throw err;
                  }
                }}
                ml={3}
              >
                Approve
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
